import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, Typography } from "@mui/material";
import { RichHTML } from "@gisce/oficina-virtual-components";
import { 
  Home, AccountBalance, AccountCircle, 
  MoneyOff, PlaylistAddCheck, WbIncandescent, Whatshot 
} from '@mui/icons-material';

import { useTranslation } from "react-i18next";
import { useForm, Controller, FormProvider } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import AddressForm from "@/components/form-fields/AddressForm/AddressForm";
import { StepConfirmationFormData, IStepForm } from "../types/stepsFormData";
import { FC, useContext, useEffect } from "react";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { Address } from "@/components/form-fields/AddressForm/types/address";
import { SubmitButtonContext } from "../Contractation";
import { getIP } from "../utils/getIP";
import useI18n from "@/hooks/useI18n";
import { useAppSelector } from "@/hooks";
import { StepElecContractFormData } from "./FormContract/types/FormContract";


const FormConfirmation: FC<IStepForm<StepConfirmationFormData>> = ({
  type,
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const { currentLang } = useI18n();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const contractation = useAppSelector(state => state[`${type}Contractation`]);
  const contractStep = contractation.steps.contract as StepElecContractFormData;

  const schema = z.object({
    acceptOVConditions: z.boolean().refine(val => {
      return isAuthenticated || val;
    }, t('common:text.required_field')),
    acceptGdpr: z.boolean().refine(val => {
      return isAuthenticated || val;
    }, t('common:text.required_field')),
    acceptPublicity: z.boolean().optional()
  }) satisfies z.ZodType<Omit<StepConfirmationFormData, "ip">>;

  const onConfirmationSubmit = async (values: StepConfirmationFormData) => {
    const ip = await getIP();
    await onSubmit({
      ...values, 
      ip
    });
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<StepConfirmationFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      acceptOVConditions: contractation.steps.confirmation.acceptOVConditions || isAuthenticated,
      acceptGdpr: contractation.steps.confirmation.acceptGdpr || isAuthenticated,
      acceptPublicity: contractation.steps.confirmation.acceptPublicity
    }
  });

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onConfirmationSubmit)()},
      loading: isSubmitting
    });
  }, [isSubmitting, handleSubmit]);

  return (
    <form onSubmit={handleSubmit(onConfirmationSubmit)}>

      <Box style={{ marginTop: 15 }}>
        <Box display={"flex"} style={{margin: 0}} alignItems="center">
          <Home fontSize="medium" color="secondary" sx={{mr: 1}}/>
          <Typography variant="h6" style={{alignSelf: "center"}}>
            {t('common:text.contractation_about_installation')}
          </Typography>
        </Box>
      </Box>

      <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
        <Grid item xs={12} sm={3} md={2}>
          <Typography variant="body1" style={{fontWeight: "bold"}}>
            {t('common:text.contractation_supply_type_validation')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9} md={10}>
            <Typography variant="body1">
            {t(`common:text.contractation_${contractation.steps.installation.residenceType}_supply`)}
          </Typography>
        </Grid>
        {Object.values(contractation.steps.installation.address)
        .some(val => !!val) &&
          <Grid container item xs={12}>
            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_address_validation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <FormProvider {...useForm<{address: Address}>({
                  defaultValues: {
                    address: contractation.steps.installation.address
                  }
              })}>
                <AddressForm readOnly={true} />
              </FormProvider>
            </Grid>
          </Grid>
        }
        <Grid item container xs={12}>
          <Grid item xs={12} sm={2}>
            <Typography variant="body1" style={{fontWeight: "bold"}}>
              {t('common:text.contractation_cnae_validation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            {contractation.steps.installation.cnae.descripcio}
          </Grid>
        </Grid>
      </Grid>

      <Box style={{ marginTop: 15 }}>
        <Box display={"flex"} style={{margin: 0}} alignItems="center">
          <AccountCircle fontSize="medium" color="secondary" sx={{mr: 1}}/>
          <Typography variant="h6" style={{alignSelf: "center"}}>
            {t('common:text.contractation_about')}{" "}
            {contractation.steps.person.company
              ? t('common:text.contractation_enterprise')
              : t('common:text.contractation_you')}
          </Typography>
        </Box>

        <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
          <Grid item xs={12} sm={3} md={2}>
            <Typography variant="body1" style={{fontWeight: "bold"}}>
              {!contractation.steps.person.company && t('common:text.contractation_name_validation')}
              {contractation.steps.person.company && t('common:text.contractation_company_validation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Typography variant="body1">
              {!contractation.steps.person.company && <>
                {contractation.steps.person.surName1} {contractation.steps.person.surName2}, {contractation.steps.person.name}
              </>}
              {contractation.steps.person.company && contractation.steps.person.name}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <Typography variant="body1" style={{fontWeight: "bold"}}>
              {t('common:text.contractation_dni_validation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Typography variant="body1">
              {contractation.steps.person.vat.replace("ES", "")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <Typography variant="body1" style={{fontWeight: "bold"}}>
              {t('common:text.contractation_mobile_validation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Typography variant="body1">
              {contractation.steps.person.mobile}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <Typography variant="body1" style={{fontWeight: "bold"}}>
              {t('common:text.contractation_email_validation')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Typography variant="body1">
              {contractation.steps.person.email}
            </Typography>
          </Grid>

          {!contractation.steps.person.sameInvoiceAddress
           && <Grid container item xs={12}>
            <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body1" style={{fontWeight: "bold"}}>
                  {t('common:text.contractation_address_validation')}
                </Typography>
              </Grid>
              <FormProvider {...useForm<{address: Address}>({
                  defaultValues: {
                    address: contractation.steps.person.ownerAddress
                  }
              })}>
              <Grid item xs={12} sm={9} md={10}>
                <AddressForm readOnly={true} />
              </Grid>
            </FormProvider>
          </Grid>}

          {contractation.steps.person.company &&
            <>
              <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body1" style={{fontWeight: "bold"}}>
                  {t('common:text.contractation_legal_representative')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9} md={10}>
                <Typography variant="body1">
                  {contractation.steps.person.surName1Representante}{" "}
                  {contractation.steps.person.surName2Representante}, {contractation.steps.person.nameRepresentante}{" "}
                  {contractation.steps.person.vatRepresentante.replace("ES", "")}
                </Typography>
              </Grid>
            </>
          }
        </Grid>
      </Box>

      {type == "electricity" &&
        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <WbIncandescent fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_about_contract')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_cups_validation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body1">
                {contractation.steps.installation.cups}
              </Typography>
            </Grid>

            {contractStep.powers.change ? <>
                <Grid item xs={12}>
                  <Typography variant="body1" style={{fontWeight: "bold"}}>
                    {t('common:text.contractation_change_power_checked')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <Typography variant="body1" style={{fontWeight: "bold"}}>
                    {t('common:text.contractation_current_power')}:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <Typography variant="body1">
                    {contractStep.powers.current.periods.map(
                      (period: number, idx: number) => {
                        const currPower = period.toLocaleString(currentLang.langCode);
                        return `P${idx+1}: ${currPower} kW `
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <Typography variant="body1" style={{fontWeight: "bold"}}>
                    {t('common:text.contractation_new_power')}:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <Typography variant="body1">
                    {contractStep.powers.new.periods.map(
                      (period: number, idx: number) => {
                        const currPower = period.toLocaleString(currentLang.langCode);
                        return `P${idx+1}: ${currPower} kW `
                    })}
                  </Typography>
                </Grid>
              </>
            :
              contractStep.powers.current.periods.some(
                period => period > 0
              ) && <>
                <Grid item xs={12} sm={3} md={2}>
                  <Typography variant="body1" style={{fontWeight: "bold"}}>
                    {t('common:text.contractation_current_power')}:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <Typography variant="body1">
                    {contractStep.powers.current.periods.map(
                      (period: number, idx: number) => {
                        const currPower = period.toLocaleString(currentLang.langCode);
                        return `P${idx+1}: ${currPower} kW `
                    })}
                  </Typography>
                </Grid>
              </>
            }

            {contractStep.accessTariff.id >= 0 && <>
              <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body1" style={{fontWeight: "bold"}}>
                  {t('common:text.contractation_new_pricelist')}:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9} md={10}>
                <Typography variant="body1">
                  {contractStep.accessTariff.name}
                </Typography>
              </Grid>
            </>}

            {contractStep.product.id >= 0 && <>
              <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body1" style={{fontWeight: "bold"}}>
                  {t('common:text.contractation_tariff')}:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9} md={10}>
                <Typography variant="body1">
                  {contractStep.product.name}
                </Typography>
              </Grid>
            </>}
            {contractStep.selfcons.enabled && <>
              <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body1" style={{fontWeight: "bold"}}>
                  {t('common:text.contractation_selfcons')}:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9} md={10}>
                <Typography variant="body1">
                  {t('common:text.generic_yes')}
                </Typography>
              </Grid>
            </>}
          </Grid>
        </Box>
      }

      {(
        type == "electricity" && contractation?.steps?.dualGasContract?.includeGas
      ) &&
        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <Whatshot fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_about_gas_contract')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_cups_validation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body1">
                {contractation?.steps?.dualGasContract.cups}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_tariff')}:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Typography variant="body1">
                {contractation?.steps?.dualGasContract.selectedProductName}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      }

      {type == "gas" &&
        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <Whatshot fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_about_gas_contract')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_cups_validation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body1">
                {contractation?.steps?.installation.cups}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_tariff')}:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Typography variant="body1">
                {contractation?.steps?.gasContract.selectedProductName}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      }
      
      {contractation.steps.payment.iban &&
        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <AccountBalance fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_about_payment')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12} sm={3}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_iban_direct_debit')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Typography variant="body1">
                {contractation.steps.payment.iban}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      }

      { type == "electricity" && 
        Settings?.features?.promotionalCode?.contractation?.enabled && 
        contractStep.promotionalCode.length > 0 &&

        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <MoneyOff fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_promotional_code_applied_title')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="body1" style={{fontWeight: "bold"}}>
                {t('common:text.contractation_promotional_code')}:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              {contractStep.promotionalCode.join(', ')}
            </Grid>
          </Grid>

        </Box>
      }

      {!isAuthenticated &&
        <Box style={{ marginTop: 15 }}>
          <Box display={"flex"} style={{margin: 0}} alignItems="center">
            <PlaylistAddCheck fontSize="medium" color="secondary" sx={{mr: 1}}/>
            <Typography variant="h6" style={{alignSelf: "center"}}>
              {t('common:text.contractation_conditions')}
            </Typography>
          </Box>

          <Grid container sx={{ml: {md: 5}, mr: {md: 5}}}>
            <Grid item xs={12}>
              <Controller
                name={"acceptOVConditions"}
                control={control}
                render={({ field }) => <FormControl>

                    <FormControlLabel
                      control={<Checkbox
                        {...field}
                        checked={field.value}
                        />} 
                      label={<RichHTML html={
                        t('common:text.contractation_ov_gdpr_accept', 
                          {
                            linkOV: Settings?.links?.conditions,
                            linkGDPR: Settings?.links?.gdpr
                          }
                        )
                      }/>} 
                    />

                    {errors.acceptOVConditions?.message &&
                      <FormHelperText error={true}>
                        {errors.acceptOVConditions.message}
                      </FormHelperText>
                    }

                  </FormControl>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={"acceptGdpr"}
                control={control}
                render={({ field }) => <FormControl>
                    
                    <FormControlLabel
                      control={<Checkbox
                        {...field}
                        checked={field.value}
                      />} 
                      label={<RichHTML html={
                        t('common:text.contractation_privacy_policy_accept', 
                          {
                            linkPrivacyPolicty: Settings?.links?.privacyPolicy,
                          }
                        )
                      }/>} 
                    />
                    
                    {errors.acceptGdpr?.message &&
                      <FormHelperText error={true}>
                        {errors.acceptGdpr.message}
                      </FormHelperText>
                    }

                  </FormControl>
                }
              />
            </Grid>

            {Settings?.contractation?.askPublicity &&
              <Grid item xs={12}>
                <Controller
                  name={"acceptPublicity"}
                  control={control}
                  render={({ field }) => <FormControlLabel
                    control={<Checkbox
                      {...field}
                      checked={field.value}
                      />} 
                      label={t('common:text.contractation_activate_publicity')}
                  />}
                />
                {errors.acceptGdpr?.message &&
                  <FormHelperText error={true}>
                    {errors.acceptGdpr.message}
                  </FormHelperText>
                }
                {t('common:text.contractation_privacy_policy')}
              </Grid>
            }
          </Grid>
        </Box>
      }

    </form>
  );
};

export default FormConfirmation;